import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FolderGrid } from "@/components/FolderGrid";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getQueue, getPendingBons, getPendingFolders } from "@/lib/offline";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, FileText, Send, ClipboardList, ScrollText, Archive, ArchiveRestore, ArrowLeft, FolderInput, FolderPlus, Folder as FolderIcon, X } from "lucide-react";

const LIMIT = 20;

const ViewTabs = ({ archived, onChange }) => (
  <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 dark:border-slate-800 dark:bg-slate-900">
    {[[false, "Actifs"], [true, "Archivés"]].map(([val, label]) => (
      <button key={label} type="button" onClick={() => onChange(val)} data-testid={`tab-${val ? "archived" : "active"}`}
        className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${archived === val
          ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
        {label}
      </button>
    ))}
  </div>
);

const InterventionCard = ({ it, isAdmin, selected, onToggle, onOpen, folderName }) => (
  <div data-testid={`intervention-card-${it.id}`} onClick={onOpen}
    className={`group relative cursor-pointer rounded-2xl border bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-amber-400 hover:shadow-lg dark:bg-slate-900 ${selected ? "border-amber-500 ring-2 ring-amber-400/40" : "border-slate-200 dark:border-slate-800"}`}>
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        {isAdmin && (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={selected} onCheckedChange={onToggle} className="h-5 w-5" data-testid={`select-${it.id}`} />
          </div>
        )}
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {it.type === "pv_liberation" ? <ScrollText className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
        </div>
      </div>
      <StatusBadge status={it.status} />
    </div>
    <p className="mt-3 line-clamp-2 font-head text-base font-bold text-slate-900 dark:text-white">{it.title}</p>
    <p className="mt-1 font-mono text-xs font-semibold text-blue-600 dark:text-blue-400">{it.reference}</p>
    {folderName && (
      <p className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500" data-testid={`card-folder-${it.id}`}>
        <FolderIcon className="h-3 w-3" /> {folderName}
      </p>
    )}
    <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-400 dark:border-slate-800">
      <span>{it.type === "pv_liberation" ? "PV libération" : "Journal chantier"}</span>
      {it.archived && <span className="inline-flex items-center gap-1 text-slate-500"><Archive className="h-3 w-3" /> Archivé</span>}
      {!it.archived && it.pdf_sent && <span className="inline-flex items-center gap-1 text-blue-600"><Send className="h-3 w-3" /> Envoyé</span>}
    </div>
  </div>
);

const SelectionBar = ({ count, allSelected, onToggleAll, archived, onApply, busy, extra }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/60">
    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
      <Checkbox checked={allSelected} onCheckedChange={onToggleAll} data-testid="select-all" />
      Tout sélectionner (page) {count > 0 && <span className="font-semibold" data-testid="selected-count">· {count} sélectionné{count > 1 ? "s" : ""}</span>}
    </label>
    <div className="flex flex-wrap items-center gap-2">
      {extra}
      <Button onClick={onApply} disabled={count === 0 || busy} data-testid={archived ? "unarchive-selected-btn" : "archive-selected-btn"}
        className="h-10 gap-2 bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900">
        {archived ? <><ArchiveRestore className="h-4 w-4" /> Désarchiver</> : <><Archive className="h-4 w-4" /> Archiver</>}
      </Button>
    </div>
  </div>
);

const PendingCard = ({ item, onOpen }) => (
  <div data-testid={`pending-card-${item.qid}`} onClick={onOpen}
    className="cursor-pointer rounded-2xl border-2 border-dashed border-amber-400 bg-amber-50/60 p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg dark:bg-amber-950/20">
    <div className="flex items-start justify-between">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-950/60">
        {item.payload?.type === "pv_liberation" ? <ScrollText className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
      </div>
      <span className="rounded-full bg-amber-500 px-2.5 py-1 text-[11px] font-semibold text-slate-900">En attente de synchro</span>
    </div>
    <p className="mt-3 line-clamp-2 font-head text-base font-bold text-slate-900 dark:text-white">
      {item.payload?.title || (item.payload?.type === "pv_liberation" ? "PV libération" : "Journal de chantier")}
    </p>
    <p className="mt-1 text-xs text-slate-500">Créé hors connexion le {new Date(item.savedAt).toLocaleString("fr-FR")}</p>
  </div>
);

const MoveSelect = ({ folders, current, disabled, onMove }) => (
  <Select value="" onValueChange={onMove} disabled={disabled}>
    <SelectTrigger className="h-10 w-48 gap-2" data-testid="move-select">
      <FolderInput className="h-4 w-4" /><SelectValue placeholder="Déplacer vers..." />
    </SelectTrigger>
    <SelectContent>
      {current !== "none" && <SelectItem value="none" data-testid="move-to-none">Sans dossier</SelectItem>}
      {folders.filter((f) => f.id !== current).map((f) => (
        <SelectItem key={f.id} value={f.id} data-testid={`move-to-${f.id}`}>{f.name}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default function InterventionsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const folder = sp.get("dossier");
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [newSignal, setNewSignal] = useState(0);
  const resetSignal = useCallback(() => setNewSignal(0), []);
  const isAdmin = user?.role === "admin";
  if (folder) return <FolderBons key={folder} folder={folder} />;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-4xl">Bons d'intervention</h1>
          <p className="mt-1 text-sm text-slate-500">Choisissez un dossier ou recherchez dans tous les dossiers.</p>
        </div>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <form onSubmit={(e) => { e.preventDefault(); setQuery(input.trim()); }} className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={input} onChange={(e) => { setInput(e.target.value); if (!e.target.value) setQuery(""); }}
            data-testid="global-search-input" placeholder="Rechercher dans tous les dossiers (réf, titre, chantier)..." className="h-11 pl-10 pr-10" />
          {input && (
            <button type="button" onClick={() => { setInput(""); setQuery(""); }} data-testid="global-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
              <X className="h-4 w-4" />
            </button>
          )}
        </form>
        {isAdmin && (
          <Button onClick={() => { setInput(""); setQuery(""); setNewSignal((n) => n + 1); }} variant="outline"
            className="h-11 shrink-0 gap-2" data-testid="new-folder-btn">
            <FolderPlus className="h-4 w-4" /> Nouveau dossier
          </Button>
        )}
      </div>
      {query
        ? <GlobalResults key={query} query={query} />
        : <FolderGrid isAdmin={isAdmin} newSignal={newSignal} onNewHandled={resetSignal} onOpen={(id) => navigate(`/interventions?dossier=${id}`)} />}
    </div>
  );
}

function GlobalResults({ query }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  const [folders, setFolders] = useState([]);

  useEffect(() => {
    api.get("/folders").then((r) => setFolders(r.data.folders)).catch(() => {});
  }, []);

  useEffect(() => {
    setRes(null);
    api.get("/interventions", { params: { search: query, page, limit: LIMIT } })
      .then((r) => setRes(r.data)).catch(() => setRes({ items: [], total: 0, pages: 1 }));
  }, [query, page]);

  const nameOf = (fid) => (fid ? folders.find((f) => f.id === fid)?.name || "Dossier" : "Sans dossier");

  if (!res) return <div className="py-16 text-center text-sm text-slate-400">Recherche...</div>;
  return (
    <div className="space-y-4" data-testid="global-results">
      <p className="text-sm text-slate-500" data-testid="global-results-count">
        {res.total} résultat{res.total > 1 ? "s" : ""} pour « {query} » dans tous les dossiers
      </p>
      {res.items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 py-12 text-center text-sm text-slate-500 dark:border-slate-700" data-testid="global-results-empty">
          Aucun bon trouvé.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {res.items.map((it) => (
            <InterventionCard key={it.id} it={it} isAdmin={false} folderName={nameOf(it.folder_id)}
              onOpen={() => navigate(`/interventions/${it.id}`)} />
          ))}
        </div>
      )}
      <Pager page={page} pages={res.pages} total={res.total} onChange={setPage} testPrefix="global-pager" />
    </div>
  );
}

function FolderBons({ folder }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [archived, setArchived] = useState(false);
  const [selected, setSelected] = useState([]);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = (p = page) => {
    setLoading(true);
    const params = { page: p, limit: LIMIT, archived, folder };
    if (status !== "all") params.status = status;
    if (type !== "all") params.type = type;
    if (search) params.search = search;
    api.get("/interventions", { params })
      .then((r) => { setItems(r.data.items); setMeta({ total: r.data.total, pages: r.data.pages }); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { setSelected([]); load(page); /* eslint-disable-next-line */ }, [status, type, archived, page]);

  useEffect(() => {
    api.get("/folders").then((r) => setFolders(r.data.folders)).catch(() => {});
  }, []);

  const isTmp = folder.startsWith("tmp_");
  const pendingBons = archived ? [] : getPendingBons(folder);
  const allFolders = [...folders, ...getPendingFolders()];
  const folderName = folder === "none" ? "Sans dossier" : (allFolders.find((x) => x.id === folder)?.name || "Dossier");
  const newBon = () => navigate(folder === "none" ? "/interventions/nouveau" : `/interventions/nouveau?dossier=${folder}`);

  const moveTo = async (target) => {
    setBusy(true);
    try {
      await api.post("/interventions/move", { ids: selected, folder_id: target === "none" ? null : target });
      const dest = target === "none" ? "Sans dossier" : folders.find((x) => x.id === target)?.name;
      toast.success(`${selected.length} bon(s) déplacé(s) vers « ${dest} »`);
      setSelected([]);
      load(page);
    } catch (e) { toast.error("Échec du déplacement"); } finally { setBusy(false); }
  };

  useEffect(() => {
    const onSync = () => load(page);
    window.addEventListener("ir-synced", onSync);
    return () => window.removeEventListener("ir-synced", onSync);
    // eslint-disable-next-line
  }, [status, type, archived, page, search]);

  const pending = getQueue().filter((q) => q.url === "/interventions" || /^\/interventions\/[^/]+$/.test(q.url)).length;

  const resetPage = (setter) => (v) => { setPage(1); setter(v); };
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = items.length > 0 && items.every((it) => selected.includes(it.id));
  const toggleAll = () => setSelected(allSelected ? [] : items.map((it) => it.id));

  const applyArchive = async () => {
    setBusy(true);
    try {
      await api.post("/interventions/archive", { ids: selected, archived: !archived });
      toast.success(`${selected.length} bon${selected.length > 1 ? "s" : ""} ${archived ? "désarchivé" : "archivé"}${selected.length > 1 ? "s" : ""}`);
      setSelected([]);
      const newPage = items.length === selected.length && page > 1 ? page - 1 : page;
      if (newPage !== page) setPage(newPage); else load(page);
    } catch (e) { toast.error("Échec de l'opération"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/interventions")} data-testid="folder-back-btn" className="shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bons d'intervention</p>
            <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-4xl" data-testid="folder-title">
              {folderName}{archived ? " — archivés" : ""}
            </h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isAdmin && <ViewTabs archived={archived} onChange={resetPage(setArchived)} />}
          <Button onClick={newBon} data-testid="new-intervention-btn"
            className="h-11 gap-2 bg-amber-500 font-semibold text-slate-900 hover:bg-amber-400">
            <Plus className="h-4 w-4" /> Nouveau bon
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <form onSubmit={(e) => { e.preventDefault(); if (page === 1) load(1); else setPage(1); }} className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-input"
            placeholder="Rechercher (réf, titre, chantier)..." className="h-11 pl-10" />
        </form>
        <Select value={status} onValueChange={resetPage(setStatus)}>
          <SelectTrigger className="h-11 w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="en_cours">En cours</SelectItem>
            <SelectItem value="termine">Terminé</SelectItem>
            <SelectItem value="brouillon">Brouillon</SelectItem>
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={resetPage(setType)}>
          <SelectTrigger className="h-11 w-44" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes trames</SelectItem>
            <SelectItem value="pv_liberation">PV libération</SelectItem>
            <SelectItem value="journal_chantier">Journal de chantier</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isAdmin && items.length > 0 && !loading && (
        <SelectionBar count={selected.length} allSelected={allSelected} onToggleAll={toggleAll}
          archived={archived} onApply={applyArchive} busy={busy}
          extra={<MoveSelect folders={folders} current={folder} disabled={selected.length === 0 || busy} onMove={moveTo} />} />
      )}

      {pending > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid="pending-sync-banner">
          {pending} bon(s) enregistré(s) sur l'appareil, en attente de synchronisation au retour du réseau.
        </div>
      )}

      {pendingBons.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="pending-bons">
          {pendingBons.map((q) => (
            <PendingCard key={q.qid} item={q} onOpen={() => navigate(`/interventions/nouveau?pending=${q.qid}`)} />
          ))}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Chargement...</div>
      ) : items.length === 0 && (pendingBons.length > 0 || isTmp) ? null : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 py-16 dark:border-slate-700" data-testid="empty-list">
          <ClipboardList className="h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">{!navigator.onLine ? "Hors ligne : cette liste n'a pas encore été consultée avec du réseau." : archived ? "Aucun bon archivé." : "Aucun bon d'intervention trouvé."}</p>
          {!archived && (
            <Button onClick={newBon} data-testid="empty-new-btn" className="mt-4 gap-2 bg-slate-900 text-white dark:bg-white dark:text-slate-900">
              <Plus className="h-4 w-4" /> Créer le premier
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((it) => (
            <InterventionCard key={it.id} it={it} isAdmin={isAdmin} selected={selected.includes(it.id)}
              onToggle={() => toggle(it.id)} onOpen={() => navigate(`/interventions/${it.id}`)} />
          ))}
        </div>
      )}

      {!loading && <Pager page={page} pages={meta.pages} total={meta.total} onChange={setPage} testPrefix="list-pager" />}
    </div>
  );
}
